import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
    GroupRideDeviceProfileContract,
    type MapboxMap,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    Vehicle,
    VehicleLocation,
} from '@polaris-customerweb/sharedcomponents.garage';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { UserProfile } from '@polaris-customerweb/sharedcomponents.user-profile';
import {
    ActiveVehicleAlert,
    LocationAlertPath,
} from '@polaris-customerweb/sharedcomponents.vehicle-health';
import { cloneDeep } from 'lodash-es';
import type { LayerSpecification, PointLike } from 'mapbox-gl';
import { selectRideGroupRiders } from 'src/app/components/ride-group/ride-group.reducer';
import { AppState } from 'src/app/models/app-state';
import { MapContentMapService } from 'src/app/services/map-content-map.service';
import { MapService } from 'src/app/services/map.service';
import { RideGroupRiderStatus } from '../models/ride-group-rider-status';
import { InProgressRideVm } from '../state/in-progress-ride/in-progress-ride.vm';
import { MapAnimationService } from './map-animation.service';
import { RideGroupService } from './ride-group.service';

@Injectable({ providedIn: 'root' })
export class RideGroupMapService extends MapContentMapService {
    private rideGroupRiders: RideGroupRiderStatus[];

    constructor(
        consoleLogService: ConsoleLogService,
        mapService: MapService,
        private mapAnimationService: MapAnimationService,
        private rideGroupService: RideGroupService,
        private store: Store<AppState>
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('groupride');

        this.store
            .pipe(select(selectRideGroupRiders))
            .subscribe((riders) => (this.rideGroupRiders = riders));
    }

    addSources(mapboxMap: MapboxMap): void {
        super.addSources(mapboxMap);

        const layerIdsAnimation = this.layerIds.filter((layerId) => {
            return layerId.toLowerCase().includes('animation');
        });

        this.swapAnimationLayers(mapboxMap, layerIdsAnimation);
    }

    setSourceDataFromModel(
        activeAlerts: ActiveVehicleAlert[],
        deviceProfiles: GroupRideDeviceProfileContract[],
        inProgressRides: readonly InProgressRideVm[],
        mapboxMap: MapboxMap,
        rideGroupRiders: RideGroupRiderStatus[],
        leader: RideGroupRiderStatus,
        userProfile: UserProfile,
        vehicleLocations: readonly VehicleLocation[],
        vehicles: readonly Vehicle[],
        locationAlertPaths: readonly LocationAlertPath[]
    ): void {
        const geoJson =
            this.rideGroupService.rideGroupRidersAndVehicleLocationToGeoJson(
                activeAlerts,
                deviceProfiles,
                inProgressRides,
                rideGroupRiders,
                leader,
                userProfile,
                vehicleLocations,
                vehicles,
                locationAlertPaths
            );

        super.setSourceData(mapboxMap, geoJson);
    }

    getRideGroupRiderAtPoint(
        mapboxMap: MapboxMap,
        point: PointLike
    ): RideGroupRiderStatus | undefined {
        const features = this.mapService.getFeaturesAtPoint(mapboxMap, point, [
            'groupRideUnselectedIcon',
        ]);
        const rider = features[0];

        if (features.length === 0) {
            return undefined;
        }

        return this.rideGroupRiders.find(
            (cachedRider) =>
                cachedRider.id === (rider.properties && rider.properties.id)
        );
    }

    private swapAnimationLayers(
        mapboxMap: MapboxMap,
        animationLayerIds: string[]
    ): void {
        animationLayerIds.forEach((layerId) => {
            const currentLayer = mapboxMap.getLayer(
                layerId
            ) as LayerSpecification;
            const filter = [...(currentLayer.filter || [])];
            const metadata = cloneDeep(currentLayer.metadata);

            const allLayerIds = mapboxMap
                .getStyle()
                .layers.map((layer) => layer.id);
            const currentLayerIndex = allLayerIds.indexOf(layerId);
            const nextLayerId = allLayerIds[currentLayerIndex + 1];

            const color = mapboxMap.getPaintProperty(layerId, 'circle-color');
            const radius = mapboxMap.getPaintProperty(layerId, 'circle-radius');

            const pulseDotName = `${layerId}PulseDot`;
            const duration = layerId.toLowerCase().includes('activealert')
                ? 1
                : 2;
            const pulseDotAnimation =
                this.mapAnimationService.createPulseDotAnimation(mapboxMap, {
                    color: color as string,
                    duration,
                    radiusEnd: radius as number,
                });

            mapboxMap.addImage(pulseDotName, pulseDotAnimation, {
                pixelRatio: pulseDotAnimation.pixelRatio,
            });

            mapboxMap.removeLayer(layerId);

            mapboxMap.addLayer(
                {
                    filter,
                    id: layerId,
                    layout: {
                        'icon-allow-overlap': true,
                        'icon-image': pulseDotName,
                    },
                    metadata,
                    source: this.sourceId,
                    type: 'symbol',
                },
                nextLayerId
            );
        });
    }
}
